import { Component, EventEmitter, Input, Output } from '@angular/core';
import { animate, keyframes, state, style, transition, trigger } from '@angular/animations';
import {
  AnimatedActionButtonConfig,
  AnimatedActionButtonViewConfig,
} from '../../../interfaces/components/animated-action-button-configuration';

@Component({
  selector: 'app-animated-action-button',
  templateUrl: './animated-action-button.component.html',
  styleUrls: ['./animated-action-button.component.scss'],
  animations: [
    trigger('textAnimationState', [
      state('idle', style({ transform: 'translateY(0)', opacity: 1 })),
      state('down', style({ transform: 'translateY(100%)', opacity: 0 })),
      state('loading', style({ transform: 'translateY(0)', opacity: 1 })),
      state('success', style({ transform: 'translateY(0)', opacity: 1 })),
      state('warning', style({ transform: 'translateY(0)', opacity: 1 })),
      transition('idle => down', [animate('300ms ease-in-out')]),
      transition('down => loading', [animate('300ms ease-in-out')]),
      transition('loading => success', [animate('300ms ease-in-out')]),
      transition('success => idle', [animate('300ms ease-in-out')]),
      transition('loading => warning', [animate('300ms ease-in-out')]),
      transition('warning => idle', [animate('300ms ease-in-out')]),
    ]),
    trigger('iconAnimationState', [
      state('none', style({ transform: 'none', opacity: 1 })),
      state('scaling', style({ transform: 'scale(1.3)', opacity: 1 })),
      state('rotating', style({ transform: 'rotate(360deg)', opacity: 1 })),
      state('fade', style({ transform: 'none', opacity: 1 })),

      transition('* => scaling', [animate('300ms ease-in-out')]),
      transition('* => rotating', [
        animate(
          '30ms ease-in-out',
          keyframes([style({ transform: 'rotate(0deg)', offset: 0 }), style({ transform: 'rotate(360deg)', offset: 1 })]),
        ),
      ]),
      transition('* => fade', [animate('300ms ease-in-out')]),
      transition('* => none', []),
    ]),
  ],
})
export class AnimatedActionButtonComponent {
  @Input() config!: AnimatedActionButtonConfig;
  @Output() actionEvent = new EventEmitter<void>();

  textAnimationState: 'idle' | 'down' | 'loading' | 'success' | 'error' | 'warning' = 'idle';
  iconAnimationState: 'none' | 'scaling' | 'rotating' | 'fade' = 'none';

  // In case of success or error, the button will take a custom class
  buttonSuccess = false;
  buttonError = false;
  buttonWarning = false;

  // In case of working, the button will take a disabled custom class
  buttonDisabled = false;

  // In case of loading, the button text will show ... (dots)
  dotsInterval: any;
  dotCount = 0;

  get currentView(): AnimatedActionButtonViewConfig {
    return this.config.views[this.textAnimationState];
  }

  // Get the icon animation state based on the current view and text state
  getIconAnimationState(): 'none' | 'scaling' | 'rotating' | 'fade' {
    if (!this.currentView || !this.currentView.animateIcon) {
      return 'none';
    }

    switch (this.textAnimationState) {
      case 'idle':
      case 'error':
      case 'warning':
        return 'fade';
      case 'loading':
        return 'none';
      case 'success':
        return 'rotating';
      case 'down':
        return 'scaling';
      default:
        return 'none';
    }
  }

  // Dots animation
  startDotsAnimation() {
    this.stopDotsAnimation();
    if (this.currentView?.withDots) {
      this.dotsInterval = setInterval(() => {
        this.dotCount = (this.dotCount + 1) % 4;
      }, 500);
    }
  }

  stopDotsAnimation() {
    if (this.dotsInterval) {
      clearInterval(this.dotsInterval);
      this.dotsInterval = null;
    }
    this.dotCount = 0;
  }

  public resolveState(result: 'success' | 'error' | 'warning'): void {
    this.stopDotsAnimation();
    this.buttonDisabled = false;
    this.textAnimationState = result;
    this.iconAnimationState = this.getIconAnimationState();
    this.buttonSuccess = result === 'success';
    this.buttonError = result === 'error';
    this.buttonWarning = result === 'warning';

    setTimeout(() => {
      this.textAnimationState = 'idle';
      this.iconAnimationState = this.getIconAnimationState();
      this.buttonSuccess = false;
      this.buttonError = false;
      this.buttonWarning = false;
    }, 5000);
  }

  /**
   * This method is called when the button is clicked.
   * It triggers the animation to go down, then to loading, etc
   */
  onClick(): void {
    this.textAnimationState = 'down';
    this.iconAnimationState = this.getIconAnimationState();

    setTimeout(() => {
      this.textAnimationState = 'loading';
      this.iconAnimationState = this.getIconAnimationState();
      this.buttonSuccess = false;
      this.buttonError = false;
      this.buttonDisabled = true;
      this.startDotsAnimation();

      this.actionEvent.emit(); // father call service then `resolveState()`
    }, 300);
  }
}
